import Analytics from '@components/charts/analytics/Analytics'
import BookingConversionSankey from '@components/charts/venue/BookingConversionSankey'
import ParametersHeatMap from '@components/charts/venue/ParametersHeatMap'
import {
  BookingConversionSankeyData,
  BookingConversionSankeyNodeKeys,
  transformToSankeyData,
} from '@components/charts/venue/sankey'
import { useAnalytics } from '@contexts/AnalyticsContext'
import { ProductType, productTypeToAnalyticsSource } from '@models/Product'
import {
  FetchVenueAnalyticsGraphParams,
  VenueAnalyticsGraphBookingConversion,
} from '@models/venue/VenueAnalyticsGraph'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { useAppSelector } from '@store/hooks'
import { selectSelectedVenue } from '@store/VenueReducer'
import {
  PageTitle,
  PageTitleProps,
} from '@supplement-bacon/react-toolkit/components'
import { Col, Empty, Row, Space } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'

interface Props {
  pageTitle?: PageTitleProps
}

const ConversionSection = ({ pageTitle }: Props) => {
  const venue = useAppSelector(selectSelectedVenue())
  const { selectedDates, selectedProduct } = useAnalytics()

  const [sankeyData, setSankeyData] = useState<BookingConversionSankeyData>()
  const [reservationSum, setReservationSum] = useState<string | number>()
  const [lostReservationSum, setLostReservationSum] = useState<
    string | number
  >()
  const [conversionRate, setConversionRate] = useState<string | number>()
  const [hasEnoughData, setHasEnoughData] = useState<boolean>(true)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const fetchData = async () => {
    setHasEnoughData(true)
    setIsLoading(true)
    setError(undefined)

    if (!selectedDates) {
      setError('No data available.')
      setIsLoading(false)
      return
    }

    // The booking-conversion graph is segmented by PostHog event `source`,
    // NOT by DB `product`. App → mobile, Chatbot → whatsapp, All → omit source
    // (aggregate funnel). See productTypeToAnalyticsSource.
    const fetchParams: FetchVenueAnalyticsGraphParams = {
      entity: 'venue',
      entityId: venue?.id,
      kind: 'booking-conversion',
      startDate: selectedDates[0],
      endDate: selectedDates[1],
      source: productTypeToAnalyticsSource(selectedProduct),
    }

    try {
      const { data } =
        await fetchAnalyticsGraph<VenueAnalyticsGraphBookingConversion[]>(
          fetchParams
        )

      // NOTE (Bot V2 stats — deferred): the Sankey node topology is currently
      // mobile-app shaped. The WhatsApp bot emits the same
      // `create-booking-tunnel-*` event names, so most funnel nodes populate,
      // but bot-specific node accuracy (e.g. the homepage/restaurants-tab
      // opening node, venue-book-pressed) is approximate when source=whatsapp.
      // Dynamic per-segment node adaptation is deferred to a dedicated session.
      const nextSankeyData = transformToSankeyData(data || [])

      setHasEnoughData(data.reduce((acc, curr) => acc + curr.count, 0) > 0)
      setSankeyData(nextSankeyData)
      const totalReservations =
        data.find((i) => i.name === BookingConversionSankeyNodeKeys.Success)
          ?.count || 0

      const totalAttempts = totalReservations + (nextSankeyData?.totalLoss || 0)

      setReservationSum(totalReservations)
      setLostReservationSum(nextSankeyData?.totalLoss || 0)
      setConversionRate(
        totalAttempts > 0
          ? parseFloat(((totalReservations / totalAttempts) * 100).toFixed(2))
          : '--'
      )

      if (!data) {
        setError('No data available.')
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('An error occurred while fetching data.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [venue, selectedDates, selectedProduct])

  const toolbar = (
    <Space>
      <Analytics.Product
        showOnlyTypes={[
          ProductType.All,
          ProductType.App,
          ProductType.Chatbot,
        ]}
      />
      <Analytics.Date
        minDate={dayjs('2025-12-01')}
        rangePresets={Analytics.rangesPresets.venue}
        defaultValue={
          Analytics.rangesPresets.venue![0].value as [Dayjs, Dayjs]
        }
      />
    </Space>
  )

  if (!hasEnoughData) {
    return (
      <Space orientation="vertical" className="w-full" size={16}>
        <PageTitle
          level={pageTitle ? pageTitle.level : 5}
          title={pageTitle ? pageTitle.title : 'Reservation conversion'}
          style={{ margin: 0 }}
          toolbar={toolbar}
        />
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={'Not enough data available.'}
        />
      </Space>
    )
  }

  return (
    <Space orientation="vertical" className="w-full" size={16}>
      <PageTitle
        level={pageTitle ? pageTitle.level : 5}
        title={pageTitle ? pageTitle.title : 'Reservation conversion'}
        style={{ margin: 0 }}
        toolbar={toolbar}
      />

      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Analytics.Statistic
            isLoading={isLoading}
            title={'Reservations'}
            value={reservationSum}
          />
        </Col>
        <Col span={6}>
          <Analytics.Statistic
            isLoading={isLoading}
            title={'Lost reservations'}
            value={lostReservationSum}
          />
        </Col>
        <Col span={6}>
          <Analytics.Statistic
            isLoading={isLoading}
            title={'Conversion rate'}
            suffix="%"
            value={conversionRate}
          />
        </Col>
        <Col span={6}>
          <Analytics.RowStatistics
            entity="venue"
            entityId={venue?.id}
            kind="venue-app-booking-conversion"
          />
        </Col>
      </Row>
      {sankeyData && (
        <BookingConversionSankey
          sankeyData={sankeyData}
          isLoading={isLoading}
          error={error}
        />
      )}
      {/* TODO: re-enable when the backend supports unscoped heatmap queries */}
      {venue && (
        <ParametersHeatMap
          venueIds={[venue.id]}
          dates={selectedDates}
          source={productTypeToAnalyticsSource(selectedProduct)}
        />
      )}
    </Space>
  )
}

export default ConversionSection
